import React, { useRef, useState, useCallback } from 'react';
import { Download } from '~/ui/assets/icons';
import { Wrapper, PlayBtn, ProgressBar, ProgressFill, TimeLabel, IconBtn, ErrorMessage } from './AudioPlayer.styled';

type AudioPlayerProps = {
  src: string;
  contentType: string;
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function PlayIcon() {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
      <path d="M1 1L13 8L1 15V1Z" fill="currentColor" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
      <rect x="1" y="1" width="4" height="14" rx="1" fill="currentColor" />
      <rect x="9" y="1" width="4" height="14" rx="1" fill="currentColor" />
    </svg>
  );
}

function VolumeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MutedIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AudioPlayer({ src, contentType }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);
  const handleError = useCallback(() => setHasError(true), []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlaying((prev) => !prev);
  }, [playing]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !muted;
    setMuted((prev) => !prev);
  }, [muted]);

  const handleTimeUpdate = useCallback(() => {
    setCurrentTime(audioRef.current?.currentTime ?? 0);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    setDuration(audioRef.current?.duration ?? 0);
  }, []);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  }, []);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
    },
    [duration],
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const timeLabel = duration > 0 ? `${formatTime(currentTime)} / ${formatTime(duration)}` : formatTime(currentTime);

  return (
    <Wrapper>
      {hasError && <ErrorMessage>Não foi possível carregar o áudio.</ErrorMessage>}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
        preload="metadata"
      >
        <source src={src} type={contentType} />
      </audio>

      <PlayBtn onClick={togglePlay} aria-label={playing ? 'Pausar' : 'Reproduzir'}>
        {playing ? <PauseIcon /> : <PlayIcon />}
      </PlayBtn>

      <ProgressBar onClick={handleProgressClick}>
        <ProgressFill progress={progress} />
      </ProgressBar>

      <TimeLabel>{timeLabel}</TimeLabel>

      <IconBtn onClick={toggleMute} aria-label={muted ? 'Ativar som' : 'Silenciar'}>
        {muted ? <MutedIcon /> : <VolumeIcon />}
      </IconBtn>

      <IconBtn as="a" href={src} download aria-label="Baixar áudio">
        <Download width={16} height={16} />
      </IconBtn>
    </Wrapper>
  );
}
